import { useEffect, useId, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { GAME, chapterSpec, fixOpensAt, solvesNeeded } from '@continuum/show-core';
import type { Alignment, GuestIntent, Meeting } from '@continuum/show-core';
import type { WireAnomaly, WireMeeting, WireState, You } from '../room/client.js';
import { Screen } from '../ui/Shell.js';
import { Eyebrow, GhostButton, Hero, Lede, PostLine, PrimaryButton, Spacer, mono, sans } from '../ui/atoms.js';
import { STOP_COPY } from '../ui/stopText.js';
import type { StopCopy } from '../ui/stopText.js';
import { ROUND_COPY } from '../ui/roundText.js';
import { FIX_COPY } from '../ui/fixText.js';
import type { FixCopy } from '../ui/fixText.js';
import { FixMission } from './Fix.js';
import { GAME_COPY } from '../ui/gameText.js';
import type { GameCopy, RuleCard, RuleLine } from '../ui/gameText.js';
import tablePlay from '../assets/tutorial/table-play.webp';
import tableFix from '../assets/tutorial/table-fix.webp';
import tableStripes from '../assets/tutorial/table-stripes.webp';
import type { Lang } from '../ui/text.js';
import { postName } from '../ui/screenText.js';
import { BottomSheet, useSheet } from '../ui/BottomSheet.js';
import { TopBanner } from '../ui/TopBanner.js';
import { PRIORITY, useBanner, useCover } from '../ui/OverlayHost.js';

/**
 * The saboteur game on a guest's phone: the volunteer question and the rules at check-in, the
 * private role card at every draw, and everything that happens over the table after that: the
 * alarms and their FIX, and the stop's own vote (Marek, 2026-10-06: nobody calls a meeting; the stop
 * meets by itself once its alarms are over, and everyone picks the guest they suspect).
 *
 * Every guest sees the same controls: CONTROLS holds MY ROLE and the language, on every phone, so a
 * neighbour glancing across learns nothing. A saboteur's sabotage is their game of the stop
 * (games/Hack.tsx), in their games list like anyone's game.
 *
 * Pop-ups follow one set of rules (ui/drag.ts): banners drop in from the top and flick up;
 * sheets rise from the bottom and pull down. The alarm and the meeting shrink to a bar at the
 * top instead of going away, so neither can be lost.
 */

const CREW = '#4fd8c2';
const AGENT = '#ff5f86';
const WARN = '#ffb547';

export function PrivatePill({ text }: { text: string }) {
  return (
    <div
      style={{
        alignSelf: 'center',
        fontFamily: mono,
        fontSize: 11,
        letterSpacing: 1.5,
        color: '#e08a3c',
        border: '1px solid rgba(224,138,60,.4)',
        borderRadius: 20,
        padding: '8px 14px',
        textAlign: 'center',
      }}
    >
      {text}
    </div>
  );
}

function useNow(ms = 500): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

function clock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The damage as five pips, filling red. */
export function Pips({ count, size = 10 }: { count: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: Math.max(2, Math.round(size / 3)) }}>
      {Array.from({ length: GAME.FRACTURE_LIMIT }, (_, i) => (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: i < count ? AGENT : 'transparent',
            border: `1px solid ${i < count ? AGENT : 'rgba(255,255,255,.35)'}`,
          }}
        />
      ))}
    </span>
  );
}

/** The table, seat by seat, with the stretch an alarm names lit up. */
function TableTrace({ from, to, total = 12 }: { from: number; to: number; total?: number }) {
  return (
    <div style={{ marginTop: 10, display: 'flex', gap: 3 }}>
      {Array.from({ length: total }, (_, i) => {
        const seat = i + 1;
        const hit = seat >= from && seat <= to;
        return (
          <span
            key={seat}
            style={{
              flex: 1,
              minWidth: 0,
              height: 22,
              borderRadius: 4,
              background: hit ? AGENT : 'rgba(255,255,255,.1)',
              display: 'grid',
              placeItems: 'center',
              fontFamily: mono,
              fontSize: 11,
              color: hit ? '#2a0610' : '#7d8e9c',
            }}
          >
            {seat}
          </span>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ check-in

export function VolunteerPick({
  lang,
  late = false,
  chosen,
  onChoose,
  onNext,
}: {
  lang: Lang;
  /** Checking in after launch: the draw has happened, and a yes counts at the next redraw. */
  late?: boolean;
  chosen: boolean | null;
  onChoose: (willing: boolean) => void;
  onNext: () => void;
}) {
  const g = GAME_COPY[lang];
  const option = (willing: boolean, title: string, sub: string, accent: string) => (
    <button
      className="cc-answer"
      onClick={() => onChoose(willing)}
      style={{
        textAlign: 'left',
        borderRadius: 12,
        border: `1px solid ${chosen === willing ? accent : `${accent}4d`}`,
        background: chosen === willing ? `${accent}1f` : `${accent}08`,
        color: '#dfe9f2',
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'block', fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 2.5, color: accent }}>
        {title}
      </span>
      <span className="cc-answer-sub" style={{ display: 'block', marginTop: 4, fontFamily: mono, fontSize: 13, color: '#9db3c2' }}>{sub}</span>
    </button>
  );

  return (
    <Screen
      style={{ background: '#070a0e', color: '#dfe9f2' }}
      footer={
        <>
          <div style={{ fontFamily: mono, fontSize: 11, color: '#6f808b', textAlign: 'center' }}>{g.volunteerNote}</div>
          <PrimaryButton onClick={onNext} disabled={chosen === null}>
            {g.continue}
          </PrimaryButton>
        </>
      }
    >
      <PrivatePill text={g.private} />
      <Eyebrow style={{ marginTop: 22 }}>{g.volunteerEyebrow}</Eyebrow>
      <h1 style={{ margin: '10px 0', fontSize: 27, lineHeight: 1.1, fontWeight: 700, letterSpacing: 1.5 }}>
        {g.volunteerTitle}
      </h1>
      <Lede>{late ? g.volunteerLedeLate : g.volunteerLede}</Lede>
      {/* The two answers fold to one line each on a short screen (styles.css `.cc-answers`). */}
      <div className="cc-answers">
        {option(true, g.volunteerYes, g.volunteerYesSub, AGENT)}
        {option(false, g.volunteerNo, g.volunteerNoSub, CREW)}
      </div>
    </Screen>
  );
}

/**
 * The table as the projector draws it, one picture a card (`RuleCard.art`), rendered by the table's
 * own code (the desktop preview's dev/table.html, 2026's colours) and cropped to a place or four:
 * - play: a place while its guest's game is still to play;
 * - fix: a place handed FIX during an alarm, the era stepped back behind it;
 * - stripes: an alarm traced to seats 3–6, seat 4 also handed FIX.
 * `focus` is the spot the card is about, in percent of the picture, ringed slowly so the eye finds it.
 */
const RULE_PICTURES: Record<RuleCard['art'], { src: string; width: number; height: number; focus?: { x: number; y: number; r: number } }> = {
  play: { src: tablePlay, width: 660, height: 297 },
  fix: { src: tableFix, width: 660, height: 318 },
  stripes: { src: tableStripes, width: 660, height: 416, focus: { x: 77.6, y: 84.4, r: 9.6 } },
};

/** The table's marks, small, beside the line that explains them: the same stripes and ring the table draws. */
function RuleMark({ mark }: { mark: RuleLine['mark'] }) {
  const id = useId().replace(/:/g, '');
  const size = 30;
  const disc = (
    <>
      <defs>
        <pattern id={`hz-${id}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
          <rect width="8" height="8" fill="#111111" />
          <rect width="4" height="8" fill="#ffd400" />
        </pattern>
      </defs>
      <circle cx="15" cy="15" r="13" fill={`url(#hz-${id})`} stroke="#000" strokeWidth="1.5" />
    </>
  );
  const ring = (r: number) => <circle cx="15" cy="15" r={r} fill="none" stroke="#f3f8fc" strokeOpacity=".9" strokeWidth="2.6" />;
  return (
    <svg width={size} height={size} viewBox="0 0 30 30" aria-hidden="true" style={{ flex: 'none', display: 'block' }}>
      {mark === 'stripes' && disc}
      {mark === 'both' && (
        <>
          {disc}
          {ring(6.5)}
        </>
      )}
      {mark === 'ring' && (
        <>
          <circle cx="15" cy="15" r="14" fill="#0b2f5c" />
          {ring(8)}
        </>
      )}
      {mark === 'film' && (
        <>
          <rect x="2" y="6" width="26" height="16" rx="2" fill="none" stroke="#9db3c2" strokeWidth="2" />
          <path d="M12.5 10.5 L19 14 L12.5 17.5 Z" fill="#9db3c2" />
          <path d="M10 26 H20" stroke="#9db3c2" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
      {mark === 'phone' && (
        <>
          <rect x="8.5" y="2.5" width="13" height="25" rx="2.5" fill="none" stroke={CREW} strokeWidth="2" />
          <path d="M13 24 H17" stroke={CREW} strokeWidth="2" strokeLinecap="round" />
        </>
      )}
      {mark === 'vote' && (
        <>
          <circle cx="15" cy="15" r="12.5" fill="none" stroke={WARN} strokeWidth="2" />
          <path d="M9.5 15.5 L13.5 19.5 L20.5 11" fill="none" stroke={WARN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
}

/**
 * The card's picture: as large as the phone leaves room for, never larger than it was rendered, and
 * smaller on a short phone so NEXT stays where it is (it shrinks before anything scrolls).
 */
function RulePicture({ art, alt }: { art: RuleCard['art']; alt: string }) {
  const pic = RULE_PICTURES[art];
  return (
    <div
      style={{
        position: 'relative',
        flex: '0 1 auto',
        minHeight: 96,
        maxWidth: '100%',
        alignSelf: 'center',
        aspectRatio: `${pic.width} / ${pic.height}`,
        maxHeight: pic.height / 2,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,.16)',
        boxSizing: 'border-box',
      }}
    >
      <img
        src={pic.src}
        alt={alt}
        draggable={false}
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
      />
      {pic.focus && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: `${pic.focus.x}%`,
            top: `${pic.focus.y}%`,
            width: `${pic.focus.r * 2}%`,
            aspectRatio: '1',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            border: `2px dashed ${CREW}`,
            boxShadow: `0 0 10px ${CREW}`,
            animation: 'ccGlow 3.2s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
}

/** The rules, one idea a card, at the guest's own pace: NEXT and BACK, or swipe sideways. */
export function HowToPlay({ lang, onDone }: { lang: Lang; onDone: () => void }) {
  const g = GAME_COPY[lang];
  const [i, setI] = useState(0);
  const card = g.rules[Math.min(i, g.rules.length - 1)]!;
  const last = i >= g.rules.length - 1;
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);

  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const up = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = swipe.current;
    swipe.current = null;
    if (!from || from.id !== e.pointerId) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (dx < 0 && !last) setI(i + 1);
    if (dx > 0 && i > 0) setI(i - 1);
  };

  return (
    <Screen
      style={{ background: 'linear-gradient(180deg,#0a0f16,#0b1118)', color: '#dfe9f2', padding: 'clamp(14px, 3.4vh, 28px) 24px clamp(12px, 2.6vh, 28px)' }}
      footer={
        <div style={{ display: 'flex', gap: 10 }}>
          {i > 0 && (
            <div style={{ flex: '0 0 36%', display: 'flex', flexDirection: 'column' }}>
              <GhostButton color="#9db3c2" border="rgba(255,255,255,.2)" onClick={() => setI(i - 1)}>
                {g.back}
              </GhostButton>
            </div>
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <PrimaryButton big onClick={() => (last ? onDone() : setI(i + 1))}>{last ? g.takeSeat : g.next}</PrimaryButton>
          </div>
        </div>
      }
    >
      <Eyebrow>{g.rulesEyebrow}</Eyebrow>
      <div style={{ marginTop: 14, display: 'flex', gap: 5 }}>
        {g.rules.map((rule, k) => (
          <span
            key={rule.title}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: k <= i ? CREW : 'rgba(255,255,255,.12)',
              transition: 'background .25s ease',
            }}
          />
        ))}
      </div>

      {/* One idea a card: its title, the table as it will look, and a line for each mark on it. */}
      <div
        key={i}
        onPointerDown={down}
        onPointerUp={up}
        onPointerCancel={() => (swipe.current = null)}
        style={{
          marginTop: 'clamp(12px, 2.6vh, 22px)',
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          animation: 'ccRise .25s ease-out',
          touchAction: 'pan-y',
        }}
      >
        {/* Sized to the phone's height, so a short phone keeps the picture readable and NEXT in reach. */}
        <h1 style={{ flex: 'none', margin: '0 0 clamp(8px, 2vh, 14px)', fontSize: 'clamp(23px, 4.4vh, 28px)', lineHeight: 1.05, fontWeight: 700, letterSpacing: 1.5 }}>
          {card.title}
        </h1>
        <RulePicture art={card.art} alt={card.alt} />
        <div style={{ flex: 'none', marginTop: 'clamp(10px, 2vh, 16px)', display: 'flex', flexDirection: 'column', gap: 'clamp(6px, 1.4vh, 12px)' }}>
          {card.lines.map((line) => (
            <div key={line.text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <RuleMark mark={line.mark} />
              <div style={{ fontFamily: sans, fontSize: 'clamp(16px, 2.9vh, 19px)', fontWeight: 600, lineHeight: 1.25 }}>{line.text}</div>
            </div>
          ))}
        </div>
      </div>
    </Screen>
  );
}

// ------------------------------------------------------------------ launch

function SheetPrimary({ children, bg, fg }: { children: ReactNode; bg?: string; fg?: string }) {
  const { close } = useSheet();
  return (
    <PrimaryButton bg={bg} fg={fg} onClick={close}>
      {children}
    </PrimaryButton>
  );
}

/**
 * The guest's role, privately. Shown by itself once at launch, and again from CONTROLS → MY ROLE
 * whenever they like. It needs its button or a long pull to close, so a stray tap never loses it.
 */
/**
 * The guest's role, privately: new at every stop (Marek, 2026-09-28), shown by itself once per draw
 * and again from CONTROLS → MY ROLE. Everyone gets the same card, HOLD TO SEE YOUR ROLE, so nobody
 * learns anything from a neighbour's screen; only while the finger is down does it show — a full red
 * YOU ARE THE SABOTEUR with the task in plain words, or the crew's. It fades in and out, and a new
 * draw buzzes the phone. GOT IT once it has been held. It needs its button or a long pull to close.
 */
export function RoleSheet({
  lang,
  alignment,
  chapter = 1,
  fresh = false,
  onDone,
}: {
  lang: Lang;
  alignment: Alignment;
  /** The era the role is for. */
  chapter?: number;
  /** A new draw has just arrived: buzz. */
  fresh?: boolean;
  onDone: () => void;
}) {
  const g = GAME_COPY[lang];
  const r = ROUND_COPY[lang];
  const agent = alignment === 'PARADOX AGENT';
  const [held, setHeld] = useState(false);
  const [seen, setSeen] = useState(false);
  const since = useRef<number | null>(null);
  useEffect(() => {
    if (fresh) navigator.vibrate?.([200, 100, 200]);
  }, [fresh]);
  const year = chapter >= 1 && chapter <= 5 ? chapterSpec(chapter as 1).era : '';
  const down = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try {
      // The finger keeps the card even if it slides off the button.
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* a pointer the browser is not tracking: the hold still works while it stays on the button */
    }
    since.current = Date.now();
    setHeld(true);
  };
  const up = () => {
    if (since.current !== null && Date.now() - since.current >= 400) setSeen(true);
    since.current = null;
    setHeld(false);
  };
  const reveal = agent ? { bg: '#c8102e', fg: '#ffffff' } : { bg: '#0e5a4f', fg: '#ffffff' };
  return (
    <BottomSheet
      id="role"
      critical
      size="full"
      zIndex={64}
      label={r.roleEyebrow(year)}
      onDismiss={onDone}
      tone={{ bg: '#05070a', fg: '#dfe9f2', border: 'rgba(255,255,255,.2)', grip: '#8fa4b5' }}
      footer={
        seen ? (
          <SheetPrimary bg="#dfe9f2" fg="#05070a">
            {r.gotIt}
          </SheetPrimary>
        ) : (
          <div style={{ fontFamily: mono, fontSize: 13, color: '#8fa4b5', textAlign: 'center', padding: '6px 0' }}>{r.heldFirst}</div>
        )
      }
    >
      <PrivatePill text={g.private} />
      <div style={{ marginTop: 22, fontFamily: mono, fontSize: 12, letterSpacing: 2, color: '#8fa4b5' }}>{r.roleEyebrow(year)}</div>
      <Hero style={{ marginTop: 10, fontSize: 38, fontWeight: 800, lineHeight: 1.04 }}>{r.holdToSee}</Hero>
      <div style={{ marginTop: 12 }}>
        <Lede>{r.roleNew}</Lede>
      </div>
      <button
        onPointerDown={down}
        onPointerUp={up}
        onPointerCancel={up}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          marginTop: 20,
          width: '100%',
          minHeight: 180,
          borderRadius: 20,
          border: '2px dashed rgba(255,255,255,.4)',
          background: 'rgba(255,255,255,.05)',
          color: '#dfe9f2',
          fontFamily: sans,
          fontSize: 28,
          fontWeight: 800,
          letterSpacing: 3,
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {r.holdToSee}
        <span style={{ display: 'block', marginTop: 8, fontFamily: mono, fontSize: 12, fontWeight: 400, letterSpacing: 1, color: '#8fa4b5' }}>{r.holdHint}</span>
      </button>
      {/* The role itself, over the whole phone while the finger is down; it never takes the touch. */}
      <div
        aria-hidden={!held}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          pointerEvents: 'none',
          opacity: held ? 1 : 0,
          visibility: held ? 'visible' : 'hidden',
          transition: 'opacity .3s ease, visibility .3s',
          background: reveal.bg,
          color: reveal.fg,
          padding: 'calc(env(safe-area-inset-top) + clamp(20px, 6vh, 48px)) 26px 24px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, opacity: 0.85 }}>{r.roleEyebrow(year)}</div>
        {/* Sized to the phone's height: the whole task fits on a short phone too, with nothing to scroll. */}
        <Hero color={reveal.fg} style={{ marginTop: 12, fontSize: 'clamp(34px, 6.6vh, 46px)', fontWeight: 800, lineHeight: 1.02 }}>
          {agent ? r.agentTitle : r.crewTitle}
        </Hero>
        <div style={{ marginTop: 'clamp(10px, 2.2vh, 18px)' }}>
          {(agent ? r.agentTask(chapter) : r.crewTask).map((line) => (
            <p key={line} style={{ margin: '0 0 clamp(6px, 1.4vh, 12px)', fontFamily: sans, fontSize: 'clamp(16px, 3vh, 20px)', fontWeight: 600, lineHeight: 1.28 }}>
              {line}
            </p>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
}

// ------------------------------------------------------------------ the toolbar

const chip = (color: string, border: string) =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
    padding: '0 10px',
    borderRadius: 22,
    border: `1px solid ${border}`,
    background: 'transparent',
    color,
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
  }) as const;

/**
 * The game's row under the header: the damage, VOTED OUT when it applies, and CONTROLS. It is
 * part of the page, so it never covers a screen's first line.
 */
export function GameToolbar({
  lang,
  state,
  you,
  onControls,
  onExplain,
}: {
  lang: Lang;
  state: WireState;
  you: You;
  onControls: () => void;
  onExplain: (text: string) => void;
}) {
  const g = GAME_COPY[lang];
  const damage = state.game.fractures;
  return (
    <>
      <button
        onClick={() => onExplain(g.damageExplain(damage, GAME.FRACTURE_LIMIT))}
        aria-label={g.damageExplain(damage, GAME.FRACTURE_LIMIT)}
        style={{ ...chip(damage > 0 ? '#ffb3c1' : '#9db3c2', 'transparent'), padding: '0 4px 0 0' }}
      >
        {g.timeline}
        <Pips count={damage} size={8} />
      </button>
      {you.brig && (
        <button onClick={() => onExplain(g.votedOutExplain)} style={chip('#ffb3c1', 'rgba(255,95,134,.55)')}>
          {g.votedOut}
        </button>
      )}
      <button onClick={onControls} style={{ ...chip('#dfe9f2', 'rgba(255,255,255,.3)'), marginLeft: 'auto' }}>
        {g.controls}
      </button>
    </>
  );
}

// ------------------------------------------------------------------ controls

/** CONTROLS: the role card again, and the language. The same on every phone, so it gives nothing away. */
function ControlsSheet({
  g,
  onShowRole,
  extra,
  onClose,
}: {
  g: GameCopy;
  onShowRole: (() => void) | null;
  /** Anything else that belongs here, e.g. the language switch. */
  extra?: ReactNode;
  onClose: () => void;
}) {
  return (
    <BottomSheet
      id="controls"
      onDismiss={onClose}
      label={g.controls}
      tone={{ bg: '#0b1016', fg: '#dfe9f2', border: 'rgba(255,255,255,.14)', grip: '#8fa4b5' }}
      header={<Eyebrow color="#8fa4b5">{g.controls}</Eyebrow>}
    >
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {onShowRole && (
          <GhostButton color="#9db3c2" border="rgba(255,255,255,.2)" onClick={onShowRole}>
            {g.myRole}
          </GhostButton>
        )}
        {extra}
        <SheetGhost label={g.close} />
      </div>
    </BottomSheet>
  );
}

function SheetGhost({ label }: { label: string }) {
  const { close } = useSheet();
  return (
    <GhostButton color="#9db3c2" border="rgba(255,255,255,.18)" onClick={close}>
      {label}
    </GhostButton>
  );
}

// ------------------------------------------------------------------ the alarm

/**
 * The alarm, for a guest not handed FIX, opened from the bar: where it came from, how long is left
 * (or the countdown before the FIX puzzle starts), who is fixing it and how many have — never who.
 * The guests handed FIX get their mission instead (screens/Fix.tsx).
 */
function AlarmCard({
  g,
  f,
  anomaly,
  crewSize,
  now,
  fixers,
  onMinimize,
}: {
  g: GameCopy;
  f: FixCopy;
  anomaly: WireAnomaly;
  crewSize: number;
  now: number;
  /** Call signs of everyone handed FIX. */
  fixers: string[];
  onMinimize: () => void;
}) {
  // Open over a game, the card covers its top: the game waits.
  useCover('alarm-card', true);
  const opensAt = fixOpensAt(anomaly);
  const counting = now < opensAt;
  return (
    <TopBanner
      tone={{ fg: AGENT, bg: '#28060c', border: AGENT }}
      role="alert"
      onDismiss={onMinimize}
      tapToDismiss={false}
      style={{ boxShadow: `0 0 40px ${AGENT}55, 0 10px 30px rgba(0,0,0,.6)` }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: 3 }}>⚠ {g.anomaly}</span>
        <span style={{ fontFamily: mono, fontSize: 18, color: '#ffd1da' }}>
          {counting ? f.othersSoon(Math.max(1, Math.ceil((opensAt - now) / 1000))) : clock(anomaly.deadline - now)}
        </span>
      </div>
      <div style={{ marginTop: 4, fontFamily: sans, fontSize: 19, fontWeight: 600, color: '#ffd1da' }}>
        {g.near(anomaly.near[0], anomaly.near[1])}
      </div>
      <TableTrace from={anomaly.near[0]} to={anomaly.near[1]} total={crewSize} />
      <div style={{ marginTop: 12, fontFamily: sans, fontSize: 17, fontWeight: 600, color: '#ffd1da', textAlign: 'center' }}>
        {f.othersSolving(fixers)}
      </div>
      <div style={{ marginTop: 8, marginBottom: 6, fontFamily: mono, fontSize: 12, lineHeight: 1.4, color: '#ffb3c1', textAlign: 'center' }}>
        {f.count(anomaly.fixes ?? 0, solvesNeeded(anomaly))}
      </div>
    </TopBanner>
  );
}

/** A bar at the top: what it is and the clock on one line, what to do under it, and › to open. */
function Bar({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 2 }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: sans, fontSize: 17, fontWeight: 700, letterSpacing: 1.5, lineHeight: 1.2 }}>{title}</span>
        <span style={{ display: 'block', marginTop: 2, fontFamily: mono, fontSize: 12, lineHeight: 1.35, color: '#f3e9ee' }}>{sub}</span>
      </span>
      <span aria-hidden="true" style={{ flex: 'none', fontSize: 22, lineHeight: 1 }}>
        ›
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ the meeting

function MeetingSheet({
  g,
  meeting,
  state,
  you,
  now,
  send,
  nameOf,
  postOf,
  onMinimize,
}: {
  g: GameCopy;
  meeting: WireMeeting;
  state: WireState;
  you: You;
  now: number;
  send: (intent: GuestIntent) => void;
  nameOf: (seat: number | null) => string;
  postOf: (seat: number | null) => string;
  onMinimize: () => void;
}) {
  const suspect = typeof meeting.suspect === 'number' ? meeting.suspect : null;
  // Everyone who can vote on this one: aboard, not voted out, and not the suspect.
  const voters = state.seats.filter((s) => s.callsign !== null && s.profession !== null && !s.brig && s.seat !== suspect);
  const vote = (option: 'yes' | 'no') => send({ type: 'vote', voteId: meeting.id, option });
  const picked = (option: string) => you.ballot === option;
  const onTrial = suspect === you.seat;
  const canVote = !you.brig && !onTrial && suspect !== null;

  return (
    <BottomSheet
      id="meeting"
      size="full"
      onDismiss={onMinimize}
      closeLabel={`▾ ${g.hide}`}
      label={g.meeting}
      tone={{ bg: '#0b0a06', fg: '#f3ead6', border: 'rgba(255,181,71,.45)', grip: WARN }}
      header={
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontFamily: sans, fontSize: 30, fontWeight: 700, letterSpacing: 4, color: WARN }}>{g.meeting}</span>
            {meeting.closesAt !== null ? <span style={{ fontFamily: mono, fontSize: 18, color: '#ffe2b0' }}>{clock(meeting.closesAt - now)}</span> : null}
          </div>
          <Eyebrow color="#b9a678">{meeting.calledBy === null ? g.calledBySteward : g.calledBy(nameOf(meeting.calledBy))}</Eyebrow>
        </>
      }
    >
      {suspect !== null && (
        <div
          style={{
            marginTop: 14,
            padding: '14px 12px',
            borderRadius: 14,
            border: `1px solid ${WARN}`,
            background: 'rgba(255,181,71,.08)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: '#b9a678' }}>
            {g.seat} {String(suspect).padStart(2, '0')}
          </div>
          <div style={{ marginTop: 2, fontFamily: sans, fontSize: 34, fontWeight: 700, letterSpacing: 3 }}>{nameOf(suspect)}</div>
          <PostLine color="#b9a678" style={{ fontSize: 12, letterSpacing: 2 }}>
            {postOf(suspect)}
          </PostLine>
          <div style={{ marginTop: 4, fontFamily: sans, fontSize: 20, fontWeight: 600, color: WARN }}>{g.suspected(nameOf(suspect))}</div>
        </div>
      )}
      <div style={{ margin: '14px 0', fontFamily: sans, fontSize: 18, lineHeight: 1.3 }}>
        {you.brig ? g.noVoteInBrig : onTrial ? g.youAreSuspect : g.meetingPrompt}
      </div>

      {canVote && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {(['yes', 'no'] as const).map((option) => {
            const mine = picked(option);
            const tone = option === 'yes' ? AGENT : CREW;
            return (
              <button
                key={option}
                onClick={() => vote(option)}
                style={{
                  minHeight: 76,
                  borderRadius: 14,
                  border: `2px solid ${mine ? tone : 'rgba(255,226,176,.25)'}`,
                  background: mine ? `${tone}2e` : 'rgba(255,226,176,.04)',
                  color: '#f3ead6',
                  fontFamily: sans,
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                {option === 'yes' ? g.voteYes : g.voteNo}
              </button>
            );
          })}
        </div>
      )}

      <Spacer min={16} />
      <div style={{ fontFamily: mono, fontSize: 13, color: '#b9a678', textAlign: 'center' }}>
        {you.ballot === 'yes' || you.ballot === 'no' ? `${g.yourVote(you.ballot === 'yes' ? g.yes : g.no)} · ` : ''}
        {g.voted(meeting.ballots ?? 0, voters.length)}
      </div>
    </BottomSheet>
  );
}

/**
 * The stop's own vote (Marek, 2026-10-06): once its alarms are over, every phone asks WHO IS THE
 * SABOTEUR?, with the clues — where each of the stop's alarms came from on the table — and everyone
 * aboard to pick from (not yourself, not anyone voted out) — no SKIP (Marek, 2026-10-10). A tap is the vote; another tap
 * changes it, until the captain's NEXT counts the votes (2026-10-09: no clock). A guest voted out watches. Pulled down, it
 * becomes a bar until the vote closes.
 */
function PickMeetingSheet({
  t,
  g,
  meeting,
  state,
  you,
  now,
  send,
  nameOf,
  postOf,
  onMinimize,
}: {
  t: StopCopy;
  g: GameCopy;
  meeting: WireMeeting;
  state: WireState;
  you: You;
  now: number;
  send: (intent: GuestIntent) => void;
  nameOf: (seat: number | null) => string;
  postOf: (seat: number | null) => string;
  onMinimize: () => void;
}) {
  const aboard = state.seats.filter((s) => s.callsign !== null && s.profession !== null && !s.brig);
  const choices = aboard.filter((s) => s.seat !== you.seat);
  const clues = state.game.anomalies.filter((a) => a.chapter === meeting.chapter);
  // The strips show tonight's crew only: the places laid (`crewSize`), and none past the last guest aboard
  // unless an alarm's trace reaches there.
  const lastAboard = Math.max(0, ...state.seats.filter((s) => s.callsign !== null && s.profession !== null).map((s) => s.seat));
  const seatsShown = Math.max(1, Math.min(state.crewSize, Math.max(lastAboard, ...clues.map((a) => a.near[1]))));
  const vote = (option: string) => send({ type: 'vote', voteId: meeting.id, option });
  const ballot = you.ballot;
  const picked = ballot !== null && /^\d+$/.test(ballot) ? Number(ballot) : null;
  const year = meeting.chapter >= 1 && meeting.chapter <= 5 ? chapterSpec(meeting.chapter).era : '';
  const status = you.brig ? '' : picked === null ? t.pickNotYet : t.pickYours(nameOf(picked));

  return (
    <BottomSheet
      id="meeting"
      size="full"
      onDismiss={onMinimize}
      closeLabel={`▾ ${g.hide}`}
      label={t.pickTitle}
      tone={{ bg: '#0b0a06', fg: '#f3ead6', border: 'rgba(255,181,71,.45)', grip: WARN }}
      header={
        <>
          <Eyebrow color="#b9a678">{t.pickEyebrow(year)}</Eyebrow>
          <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontFamily: sans, fontSize: 'clamp(24px, 4.6vh, 30px)', fontWeight: 800, letterSpacing: 1.5, lineHeight: 1.05, color: WARN }}>{t.pickTitle}</span>
            {/* The stop's vote has no clock (2026-10-09): it is open until the captain's NEXT counts it. */}
            {meeting.closesAt !== null ? <span style={{ flex: 'none', fontFamily: mono, fontSize: 18, color: '#ffe2b0' }}>{clock(meeting.closesAt - now)}</span> : null}
          </div>
        </>
      }
      footer={
        <>
          <div style={{ fontFamily: mono, fontSize: 13, color: '#b9a678', textAlign: 'center' }}>
            {status ? `${status} · ` : ''}
            {t.voted(meeting.ballots ?? 0, aboard.length)}
          </div>
        </>
      }
    >
      {/* the clues: where the stop's alarms came from, on the table's seats */}
      <div style={{ marginTop: 6, fontFamily: mono, fontSize: 11, letterSpacing: 2, color: '#b9a678' }}>{t.pickClues(clues.length)}</div>
      {clues.length === 0 ? (
        <div style={{ marginTop: 6, fontFamily: sans, fontSize: 17, fontWeight: 600, color: '#ffe2b0' }}>{t.pickNoClues}</div>
      ) : (
        clues.map((a) => <TableTrace key={a.id} from={a.near[0]} to={a.near[1]} total={seatsShown} />)
      )}
      <div style={{ margin: '14px 0 10px', fontFamily: sans, fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>{you.brig ? t.pickOut : t.pickPrompt}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
        {choices.map((s) => {
          const chosen = picked === s.seat;
          return (
            <button
              key={s.seat}
              disabled={you.brig}
              onClick={() => vote(String(s.seat))}
              style={{
                minHeight: 60,
                borderRadius: 12,
                border: `2px solid ${chosen ? WARN : 'rgba(255,226,176,.22)'}`,
                background: chosen ? 'rgba(255,181,71,.22)' : 'rgba(255,226,176,.04)',
                color: '#f3ead6',
                textAlign: 'left',
                padding: '6px 12px',
                minWidth: 0,
                opacity: you.brig ? 0.55 : 1,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ fontFamily: sans, fontSize: 18, fontWeight: 700, letterSpacing: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.callsign}
                </span>
                <span style={{ flex: 'none', fontFamily: mono, fontSize: 11, color: '#b9a678' }}>{String(s.seat).padStart(2, '0')}</span>
              </span>
              <PostLine color="#b9a678">{postOf(s.seat)}</PostLine>
            </button>
          );
        })}
      </div>
      <Spacer min={12} />
    </BottomSheet>
  );
}

// ------------------------------------------------------------------ everything over the table

/**
 * Everything the game puts over the show once the ship has launched: an alarm the moment one
 * opens, the vote when a meeting is called, the verdict after, the turns in the story, and the
 * CONTROLS sheet. Banners go to the one top slot (ui/OverlayHost.tsx) by priority.
 */
export function GameLayer({
  lang,
  state,
  you,
  send,
  controlsOpen,
  onCloseControls,
  onShowRole,
  extra,
  onUrgent,
}: {
  lang: Lang;
  state: WireState;
  you: You;
  send: (intent: GuestIntent) => void;
  extra?: ReactNode;
  /** An alarm needs this phone's FIX: close whatever sheet is in the way. */
  onUrgent: () => void;
  controlsOpen: boolean;
  onCloseControls: () => void;
  /** Opens the role card again (null before roles are drawn). */
  onShowRole: (() => void) | null;
}) {
  const g = GAME_COPY[lang];
  const t = STOP_COPY[lang];
  const now = useNow(500);
  const game = state.game;
  // Pop-ups put away, by what they were about, so a new alarm or meeting still comes up.
  const [aside, setAside] = useState<Record<string, true>>({});
  const put = (key: string) => setAside((a) => ({ ...a, [key]: true }));
  const bring = (key: string) => setAside(({ [key]: _gone, ...rest }) => rest);

  const nameOf = (seat: number | null): string => {
    if (seat === null) return '';
    return state.seats.find((s) => s.seat === seat)?.callsign ?? `${g.seat} ${seat}`;
  };
  const postOf = (seat: number | null): string => postName(state.seats.find((s) => s.seat === seat)?.profession, lang);

  const open = game.anomalies.find((a) => a.stabilisedAt === null && a.fracturedAt === null) ?? null;
  // Only the seats the room handed FIX get the card; everyone else gets the alarm as a bar.
  const onFix = open !== null && open.called.includes(you.seat);
  const fixers = open ? open.called.filter((seat) => !onFix || seat !== you.seat).map(nameOf) : [];
  const answered = open ? open.seen[String(you.seat)] !== undefined : false;
  // A phone handed FIX tells the room the moment it is awake to show it. One that is asleep never
  // does, and after a few seconds the room hands FIX to the next seat as well.
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !onFix || answered) return;
    const id = open.id;
    const say = () => {
      if (document.visibilityState !== 'visible' || told.current === id) return;
      told.current = id;
      send({ type: 'alarmSeen', anomaly: id });
    };
    say();
    document.addEventListener('visibilitychange', say);
    return () => document.removeEventListener('visibilitychange', say);
  }, [open?.id, onFix, answered, send]);

  const last = game.anomalies[game.anomalies.length - 1];
  const settledAt = last ? (last.stabilisedAt ?? last.fracturedAt) : null;
  // Waits its turn behind anything more urgent, for up to a minute.
  const flash = last && settledAt !== null && now - settledAt < 60_000 && !aside[`flash:${last.id}`] ? last : null;
  const meeting = game.meeting;
  const lastMeeting: Meeting | undefined = game.meetings[game.meetings.length - 1];
  // The stop's own vote has a sheet of its own (Round.tsx `VerdictSheet`); a meeting the captain called keeps its banner.
  const verdict =
    lastMeeting?.result && !lastMeeting.pick && lastMeeting.closedAt !== null && now - lastMeeting.closedAt < 60_000 && !aside[`verdict:${lastMeeting.id}`]
      ? lastMeeting.result
      : null;

  // A meeting takes the screen: CONTROLS steps aside for it.
  useEffect(() => {
    if (meeting && controlsOpen) onCloseControls();
  }, [meeting, controlsOpen, onCloseControls]);

  // An alarm that needs this phone's FIX comes first: any sheet in the way closes, once per alarm.
  const urgentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !onFix || meeting || urgentFor.current === open.id) return;
    urgentFor.current = open.id;
    onUrgent();
  }, [open, onFix, meeting, onUrgent]);

  // ---- the alarm: the FIX mission, full screen, for the seats handed FIX (screens/Fix.tsx); a bar
  // for everyone else, which opens a card with where it came from and how many have fixed it
  const f = FIX_COPY[lang];
  const alarmKey = open ? `alarm:${open.id}` : '';
  const lookKey = open ? `look:${open.id}` : '';
  const mission = Boolean(open && !meeting && onFix && !aside[alarmKey]);
  const alarmCard =
    open && !meeting && !onFix && aside[lookKey] ? (
      <AlarmCard
        g={g}
        f={f}
        anomaly={open}
        crewSize={state.crewSize}
        now={now}
        fixers={fixers}
        onMinimize={() => bring(lookKey)}
      />
    ) : null;
  useBanner(open ? `alarm-card:${open.id}` : 'alarm-card', PRIORITY.fix, alarmCard);
  const countdown = open ? Math.max(1, Math.ceil((fixOpensAt(open) - now) / 1000)) : 0;
  const counting = open !== null && now < fixOpensAt(open);
  const mineSolved = open !== null && (you.solved ?? []).includes(open.id);
  const fixSub = !open
    ? ''
    : onFix
      ? counting
        ? f.barSoon(countdown)
        : mineSolved
          ? f.barDone
          : f.barSolve
      : counting
        ? f.othersSoon(countdown)
        : g.fixersShort(fixers);

  // One alarm per saboteur at the stop's end, one after another: which one this is.
  const stop = game.stop && game.stop.chapter === state.chapter ? game.stop : null;
  const alarmOf = stop && stop.alarms > 1 && stop.step === 'alarms' ? g.alarmOf(Math.max(1, stop.fired), stop.alarms) : '';
  const alarmBar =
    open && !meeting && !alarmCard && !mission && !aside[`bar:${open.id}`] ? (
      <TopBanner
        compact
        role="alert"
        tone={{ fg: AGENT, bg: '#1c0508', border: AGENT }}
        onOpen={() => (onFix ? bring(alarmKey) : put(lookKey))}
        // The bar of a guest on FIX stays until the alarm is over; anyone else can flick it away.
        onDismiss={onFix ? undefined : () => put(`bar:${open.id}`)}
        tapToDismiss={false}
      >
        <Bar title={`⚠ ${g.anomaly}${alarmOf ? ` ${alarmOf}` : ''} · ${clock(open.deadline - now)}`} sub={fixSub} />
      </TopBanner>
    ) : null;
  useBanner(open ? `alarm-bar:${open.id}` : 'alarm-bar', onFix ? PRIORITY.fix : PRIORITY.alarm, alarmBar);

  // ---- the meeting: a sheet, or its bar once the guest pulls it down
  const meetingKey = meeting ? `meeting:${meeting.id}` : '';
  const votersLeft = meeting
    ? state.seats.filter((s) => s.callsign && s.profession && !s.brig && s.seat !== meeting.suspect).length
    : 0;
  const pick = Boolean(meeting?.pick);
  const mustVote =
    meeting !== null &&
    !you.brig &&
    (pick ? you.ballot === null : meeting.suspect !== you.seat && meeting.suspect !== null && you.ballot !== 'yes' && you.ballot !== 'no');
  const myPick = you.ballot !== null && /^\d+$/.test(you.ballot) ? nameOf(Number(you.ballot)) : null;
  const meetingBar =
    meeting && aside[meetingKey] && !aside[`meetingbar:${meeting.id}`] ? (
      <TopBanner
        compact
        tone={{ fg: WARN, bg: '#1a1305', border: WARN }}
        onOpen={() => bring(meetingKey)}
        // Until this phone has voted, the bar stays.
        onDismiss={mustVote ? undefined : () => put(`meetingbar:${meeting.id}`)}
        tapToDismiss={false}
      >
        <Bar
          title={meeting.closesAt === null ? (pick ? t.pickTitle : g.meeting) : `${pick ? t.pickTitle : g.meeting} · ${clock(meeting.closesAt - now)}`}
          sub={
            mustVote
              ? pick
                ? t.pickBar
                : g.voteNow
              : pick && !you.brig
                ? t.pickBarVoted(myPick)
                : g.voted(meeting.ballots ?? 0, votersLeft)
          }
        />
      </TopBanner>
    ) : null;
  useBanner(meeting ? `meeting-bar:${meeting.id}` : 'meeting-bar', PRIORITY.meeting, meetingBar);

  // ---- FIXED or DAMAGE
  const flashNode = flash ? (
    <TopBanner
      tone={
        flash.stabilisedAt !== null ? { fg: CREW, bg: '#06221e', border: CREW } : { fg: AGENT, bg: '#28060c', border: AGENT }
      }
      autoHideMs={6_000}
      onDismiss={() => put(`flash:${flash.id}`)}
    >
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 1.5, textAlign: 'center', paddingBottom: 4 }}>
        {flash.stabilisedAt !== null ? g.stabilised : g.fractured(game.fractures, GAME.FRACTURE_LIMIT)}
      </div>
    </TopBanner>
  ) : null;
  useBanner(flash ? `flash:${flash.id}` : 'flash', PRIORITY.flash, flashNode);

  // ---- the verdict
  const verdictNode =
    verdict && !meeting && lastMeeting ? (
      <TopBanner
        tone={{ fg: verdict.caughtAgent ? CREW : WARN, bg: '#0c0a06', border: verdict.caughtAgent ? CREW : WARN }}
        autoHideMs={12_000}
        onDismiss={() => put(`verdict:${lastMeeting.id}`)}
      >
        <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 1, lineHeight: 1.25, textAlign: 'center', paddingBottom: 4 }}>
          {verdict.sentToBrig === null
            ? g.nobodyToBrig
            : g.toBrig(nameOf(verdict.sentToBrig), verdict.caughtAgent)}
        </div>
      </TopBanner>
    ) : null;
  useBanner(lastMeeting ? `verdict:${lastMeeting.id}` : 'verdict', PRIORITY.verdict, verdictNode);

  // ---- the story: The Boom falls to the saboteurs, and the fight after it starts at no damage
  // At the stop's end the machines have a step of their own; the vote comes after it and takes the screen.
  const machinesNow = stop ? stop.step === 'machines' : now - (game.uprising ?? 0) < 120_000;
  const uprising =
    game.uprising !== null && state.chapter === 2 && machinesNow && !aside.uprising ? (
      <Story title={g.uprisingTitle} body={g.uprisingBody} color={AGENT} background="#28060c" onAway={() => put('uprising')} />
    ) : null;
  useBanner('story:uprising', PRIORITY.story, uprising);

  return (
    <>
      {mission && open && (
        <FixMission
          key={open.id}
          lang={lang}
          state={state}
          anomaly={open}
          you={you}
          now={now}
          others={fixers}
          send={send}
          onMinimize={() => put(alarmKey)}
        />
      )}
      {meeting && meeting.pick && !aside[meetingKey] && (
        <PickMeetingSheet
          key={meeting.id}
          t={t}
          g={g}
          meeting={meeting}
          state={state}
          you={you}
          now={now}
          send={send}
          nameOf={nameOf}
          postOf={postOf}
          onMinimize={() => put(meetingKey)}
        />
      )}
      {meeting && !meeting.pick && !aside[meetingKey] && (
        <MeetingSheet
          key={meeting.id}
          g={g}
          meeting={meeting}
          state={state}
          you={you}
          now={now}
          send={send}
          nameOf={nameOf}
          postOf={postOf}
          onMinimize={() => put(meetingKey)}
        />
      )}
      {controlsOpen && !meeting && (
        <ControlsSheet g={g} onShowRole={onShowRole} extra={extra} onClose={onCloseControls} />
      )}
    </>
  );
}

/** A turn in the story, over the show: a headline and what it means, in plain words. */
function Story({
  title,
  body,
  color,
  background,
  onAway,
}: {
  title: string;
  body: string;
  color: string;
  background: string;
  onAway: () => void;
}) {
  return (
    <TopBanner tone={{ fg: color, bg: background, border: color }} autoHideMs={30_000} onDismiss={onAway}>
      <div style={{ textAlign: 'center', paddingBottom: 4 }}>
        <div style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: 1.5 }}>{title}</div>
        <div style={{ marginTop: 8, fontFamily: sans, fontSize: 17, fontWeight: 500, lineHeight: 1.35, color: '#f3e9ee' }}>{body}</div>
      </div>
    </TopBanner>
  );
}
